import { Fragment, useMemo, useRef } from "react"
import type { FC, ReactElement, ReactNode, RefObject } from "react"

import { AnimatePresence, type Transition, motion, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNull from "lodash-es/isNull"
import type { Variants as MotionVariants } from "motion-dom"

import {
    Button,
    EButtonVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    EWaveLoaderVariant,
    InfoBox,
    Typography,
    WaveLoader
} from "@/3514/components"
import { useIsFetching, useIsMutating } from "@/3514/hooks"
import { EFeedbackModalType, EFeedbackModalVariant } from "@/3514/modals"
import { fireActionDatadog, getCssIdentifierFromFileName, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { ECoachingChatMutationKey as EMutationKey, ECoachingChatQueryKey as EQueryKey } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import { type TCoachingChatConversationStarter as TConversationStarter } from "../hooks"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationMessagesPresenceSelector as useConversationMessagesPresenceSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatFormErrorVariant as EFormErrorVariant
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

import { CoachingChatReportFeedbackOpener as ReportFeedbackOpener } from "./coaching-chat.report-feedback-opener.component"

const displayName: string = "CoachingChatBanner"

const containerAnimationCssIdentifier: string = getCssIdentifierFromFileName(displayName, "cn-a")
const contentAnimationCssIdentifier: string = getCssIdentifierFromFileName(displayName, "ct-a")
const loadingAnimationCssIdentifier: string = getCssIdentifierFromFileName(displayName, "la-k")

const { getChatBannerSubtitleMessage: getSubtitleMessage } = uiUtils

const {
    conversation: { banner: classes }
} = chatStyles

const crossfadeMotionVariants: MotionVariants = {
    initial: { opacity: 0, filter: "blur(100px)" },
    animate: { opacity: 1, filter: "blur(0px)" },
    exit: { opacity: 0, filter: "blur(100px)" }
}

const Banner: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const isMotionReduced: boolean = useReducedMotion()

    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    const wrapperRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const {
        entityId,
        chapterId,
        feedback: { handleModalOpen: handleFeedbackModalOpen },
        conversation: { starterHook: useConversationStarter }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const {
        startConversation,
        isError: isMutationError,
        isLoading: isMutationLoading,
        isSuccess: isMutationSuccess,
        failureCount
    }: TConversationStarter = useConversationStarter()

    const chatStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const connectionApiMode: EConversationApiMode = useConversationApiMode()

    const isConversationFetching: boolean = useIsFetching(EQueryKey.Conversation(conversationId))

    const areMessagesPresent: boolean = useConversationMessagesPresenceSelector(entityId)

    const isBannerVisible: boolean =
        !isConversationFetching &&
        includes<EConversationStatus>([EConversationStatus.Locked, EConversationStatus.ReadyToStart], chatStatus) &&
        connectionApiMode !== null &&
        !areMessagesPresent

    const { error: formError, isFilling: isFormFilling }: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const isSeedMutationLoading: boolean = useIsMutating(EMutationKey.Seed(chapterId))

    const isViolationError: boolean = eq(formError.variant, EFormErrorVariant.ContentViolation)

    const framerTransition: Transition = useMemo(
        (): Transition => ({ duration: isMotionReduced ? 0 : 0.15, ease: [0.22, 1, 0.36, 1] }),
        [isMotionReduced]
    )

    const handleButtonAction: ReturnType<typeof withPressHandler> = withPressHandler(async (): Promise<void> => {
        fireActionDatadog("CoachingChat start attempt")

        await startConversation()
    })

    function handleFormErrorAction(): void {
        handleFeedbackModalOpen({
            variant: isViolationError ? EFeedbackModalVariant.SeedViolation : EFeedbackModalVariant.SeedApiError,
            type: EFeedbackModalType.Report,
            payload: { moderationId: formError.moderationId }
        })
    }

    function renderSubtitleMessage(): ReactNode {
        return (
            <Typography
                text={getSubtitleMessage({
                    chatStatus,
                    isSeedMutationLoading,
                    isConversationMutationError: isMutationError,
                    isFormFilling,
                    t
                })}
                alignment={ETypographyAlignment.Center}
                color={ETypographyColor.White}
                size={ETypographySize.Small}
                tag={ETypographyTag.Paragraph}
                className={classes.hint()}
            />
        )
    }

    function renderErrorFormBox(): ReactNode {
        return (
            <InfoBox
                variant="warning"
                className={classes.formErrorBox(formError.variant)}
                title={
                    isViolationError
                        ? t("entities.coachingChat.chat.banner.formError.violation.title")
                        : t("entities.coachingChat.chat.banner.formError.api.title")
                }
                message={
                    isViolationError ? formError.message : t("entities.coachingChat.chat.banner.formError.api.message")
                }
                {...(isViolationError
                    ? {}
                    : {
                          action: {
                              onClick: withPressHandler(handleFormErrorAction),
                              children: t("entities.coachingChat.chat.banner.formError.api.action"),
                              textWeight: ETypographyWeight.Bold
                          }
                      })}
            />
        )
    }

    return isBannerVisible ? (
        <AnimatePresence mode="wait">
            {!isMutationSuccess ? (
                <motion.div
                    key={
                        !isMutationSuccess
                            ? `${containerAnimationCssIdentifier}-idle`
                            : `${containerAnimationCssIdentifier}-succ`
                    }
                    ref={containerRef}
                    className={classes.container()}
                    variants={{
                        initial: { opacity: 0, filter: "blur(100px)" },
                        animate: { opacity: 1, filter: "blur(0px)" },
                        exit: { opacity: 0, filter: "blur(100px)" }
                    }}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={framerTransition}
                >
                    <AnimatePresence mode="wait">
                        {isMutationLoading ? (
                            <motion.div
                                key={`${contentAnimationCssIdentifier}-loading`}
                                ref={wrapperRef}
                                variants={crossfadeMotionVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={framerTransition}
                                className={classes.contentWrapper(formError.variant)}
                            >
                                <WaveLoader
                                    cssIdentifier={loadingAnimationCssIdentifier}
                                    variant={EWaveLoaderVariant.WhiteBlue}
                                />
                            </motion.div>
                        ) : (
                            <motion.div
                                key={`${contentAnimationCssIdentifier}-idle`}
                                variants={crossfadeMotionVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={framerTransition}
                                className={classes.contentWrapper(formError.variant)}
                            >
                                <div
                                    className={classes.contentHeadingWrapper({
                                        isError: !isNull(formError)
                                    })}
                                >
                                    {isViolationError || (
                                        <Typography
                                            text={t("entities.coachingChat.chat.banner.title")}
                                            size={ETypographySize.Heading2}
                                            alignment={ETypographyAlignment.Center}
                                            tag={ETypographyTag.Heading2}
                                            weight={ETypographyWeight.Bold}
                                            color={ETypographyColor.Gold}
                                            className={classes.title()}
                                        />
                                    )}

                                    {isNull(formError.variant) ? renderSubtitleMessage() : renderErrorFormBox()}

                                    {isViolationError && (
                                        <ReportFeedbackOpener
                                            variant="seeds"
                                            moderationId={formError.moderationId}
                                            modalVariant={
                                                isViolationError
                                                    ? EFeedbackModalVariant.SeedViolation
                                                    : EFeedbackModalVariant.SeedApiError
                                            }
                                        />
                                    )}
                                </div>

                                {isViolationError || (
                                    <Button
                                        variant={EButtonVariant.SolidDark}
                                        className={classes.button()}
                                        onClick={handleButtonAction}
                                        onKeyDown={handleButtonAction}
                                        isDisabled={
                                            isFormFilling ||
                                            !eq(chatStatus, EConversationStatus.ReadyToStart) ||
                                            isSeedMutationLoading ||
                                            failureCount >= 1
                                        }
                                        isCentered
                                    >
                                        {t("entities.coachingChat.chat.banner.button")}
                                    </Button>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            ) : (
                <Fragment />
            )}
        </AnimatePresence>
    ) : null
}

Banner.displayName = displayName

export { Banner as CoachingChatBanner }
